import * as React from 'react'
import { ReactElement, useState } from 'react'
import { Stack, StackItem } from '@fluentui/react'
import { Badge, Button, Text, Tooltip } from '@fluentui/react-components'
import { formatShortDate } from '../../../../helpers/dateHelper'
import { BankTransactionDto } from '../../../../config/finance/invoiceConfig'
import { CalendarMonthRegular, EditRegular, PaymentRegular } from '@fluentui/react-icons'
import MoneyText from '../../../Text/Currency/MoneyText'
import { TreeItem, TreeItemLayout } from '@fluentui/react-components/unstable'
import BankTransactionDialog from './BankTransaction/Dialog/BankTransactionDialog'
import DeleteBankTransactionButton from '../../../Button/DeleteBankTransactionButton'
import useAxios from '../../../../hooks/useAxios'
import { BankTransactionsProps } from './BankTransactions'
import { useTranslation } from 'react-i18next'

export type BankTransactionProps = BankTransactionsProps & {
    bankTransaction: BankTransactionDto
    index: number
    remove: (removeIndex: number) => void
}

function Actions(props: BankTransactionProps & { setEdit: (edit: boolean) => void }): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    return (
        <Stack horizontal verticalAlign="center">
            <StackItem>
                <Tooltip
                    content={translate('invoice.edit.form.banktransactions.edit.action')}
                    relationship="description"
                    withArrow
                >
                    <Button
                        appearance="transparent"
                        icon={<EditRegular />}
                        onClick={event => {
                            event.stopPropagation()
                            props.setEdit(true)
                        }}
                    />
                </Tooltip>
            </StackItem>
            <StackItem>
                <Tooltip
                    content={translate('invoice.edit.form.banktransactions.remove.action')}
                    relationship="description"
                    withArrow
                >
                    <DeleteBankTransactionButton
                        onConfirm={setOpen => {
                            axios
                                .delete(`/invoice/${props.invoice.id}/bank-transaction/${props.bankTransaction.id}`)
                                .then(() => {
                                    props.remove(props.index)
                                    setOpen(false)
                                })
                        }}
                    />
                </Tooltip>
            </StackItem>
        </Stack>
    )
}

export default function BankTransaction(props: BankTransactionProps): ReactElement {
    const [edit, setEdit] = useState(false)

    return (
        <>
            <TreeItem itemType={'leaf'} onClick={event => edit && event.preventDefault()}>
                <TreeItemLayout
                    iconBefore={<PaymentRegular />}
                    actions={<Actions {...props} setEdit={setEdit} />}
                    aside={
                        <MoneyText
                            value={{ amount: props.bankTransaction.value, currency: props.invoice?.price?.currency }}
                            textProps={{ size: 400 }}
                        />
                    }
                >
                    <Stack
                        horizontal={false}
                        verticalAlign="center"
                        tokens={{ childrenGap: 's2 s1', padding: 's2 0' }}
                        wrap
                    >
                        <Text>{props.bankTransaction.description}</Text>
                        <Stack horizontal tokens={{ childrenGap: 's2' }}>
                            <Badge size="medium" icon={<CalendarMonthRegular />} appearance="tint">
                                {formatShortDate(props.bankTransaction.date)}
                            </Badge>
                        </Stack>
                    </Stack>
                </TreeItemLayout>
            </TreeItem>
            {edit && props.isFinal && (
                <BankTransactionDialog
                    {...props}
                    key={`dialog-transaction-${props.bankTransaction.id}`}
                    open={edit}
                    setOpen={setEdit}
                    isNew={false}
                />
            )}
        </>
    )
}
